import { motion, useReducedMotion } from 'framer-motion'
import { ArrowUpRight, Clapperboard } from 'lucide-react'
import type { InstagramReel } from '@/types'
import { instagramEmbedUrl, instagramProfileLabel, instagramProfileUrl } from '@/lib/instagramMedia'
import { InstagramBrandIcon } from '@/components/icons/SocialBrandIcons'
import { TextLink } from '@/store/ui'
import { FeaturedVideosScroller } from './FeaturedVideoCard'
import { homeEase, homeFadeUp } from './motion'

interface HomeFeaturedVideosProps {
  reels: InstagramReel[]
  instagram?: string
}

export function HomeFeaturedVideos({ reels, instagram }: HomeFeaturedVideosProps) {
  const reduceMotion = useReducedMotion()
  const visible = reels.filter((reel) => instagramEmbedUrl(reel.url))

  if (visible.length === 0) return null

  const profileHref = instagramProfileUrl(instagram)
  const profileLabel = instagramProfileLabel(instagram)

  return (
    <section
      id="featured-videos"
      className="relative overflow-hidden bg-[#FAF8F4]"
      aria-labelledby="featured-videos-title"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <div className="absolute inset-0 bg-gradient-to-b from-white via-[#FAF8F4] to-[#F3EFE8]" />
        <div className="absolute inset-x-0 top-0 h-24 bg-[radial-gradient(ellipse_80%_100%_at_50%_0%,rgba(249,224,118,0.12),transparent)]" />
        <div className="absolute -right-20 top-10 h-48 w-48 rounded-full bg-gold-300/10 blur-3xl" />
        <div className="absolute -left-16 bottom-0 h-40 w-40 rounded-full bg-amber-200/10 blur-3xl" />
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold-500/30 to-transparent" />

      <div className="store-container relative py-10 md:py-14">
        <motion.header
          initial={reduceMotion ? false : 'hidden'}
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={homeFadeUp}
          transition={{ duration: 0.5, ease: homeEase }}
          className="mb-8 flex flex-col gap-4 sm:mb-10 sm:flex-row sm:items-end sm:justify-between"
        >
          <div className="max-w-2xl">
            <p className="type-eyebrow mb-3 inline-flex items-center gap-1.5">
              <Clapperboard className="h-4 w-4 text-gold-600" aria-hidden />
              From our Instagram
            </p>
            <h2
              id="featured-videos-title"
              className="font-display text-xl font-bold tracking-tight gold-gradient-text sm:text-2xl md:text-3xl lg:text-4xl"
            >
              Featured Videos
            </h2>
            <p className="mt-3 type-body-muted text-sm md:text-base">
              Behind-the-scenes pours, scent stories, and moments from the Ooudh Kraft
              studio — tap a video to play.
            </p>
          </div>

          <motion.a
            href={profileHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Follow ${profileLabel} on Instagram`}
            className="btn-primary shrink-0 self-start rounded-full px-4 py-2 focus-ring sm:self-auto"
            initial={reduceMotion ? false : { opacity: 0, scale: 0.92, y: 10 }}
            whileInView={reduceMotion ? undefined : { opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.45, ease: homeEase, delay: 0.12 }}
            whileHover={
              reduceMotion
                ? undefined
                : { scale: 1.05, y: -2, transition: { duration: 0.2, ease: homeEase } }
            }
            whileTap={reduceMotion ? undefined : { scale: 0.97 }}
          >
            <InstagramBrandIcon className="h-4 w-4 text-white" aria-hidden />
            Follow
          </motion.a>
        </motion.header>

        <motion.div
          initial={reduceMotion ? false : 'hidden'}
          whileInView="visible"
          viewport={{ once: true, margin: '-40px' }}
          variants={homeFadeUp}
          transition={{ duration: 0.5, ease: homeEase, delay: 0.05 }}
        >
          <FeaturedVideosScroller reels={visible} />
        </motion.div>

        <div className="mt-6 flex justify-center sm:hidden">
          <TextLink
            to={profileHref}
            external
            className="inline-flex items-center gap-1 text-sm"
          >
            More on Instagram <ArrowUpRight className="h-4 w-4" aria-hidden />
          </TextLink>
        </div>
      </div>
    </section>
  )
}
